import { useCallback, useEffect, useState } from "react"
import { IconLoader2, IconRefresh, IconUserOff } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { slotTypeLabelForSlug } from "@/app/booking-calendar/lib/slotTypes"
import { api } from "@/lib/api"

// ── Types ────────────────────────────────────────────────────────────
// Mirrors UnassignedHostBooking / UnassignedHostBookingReport in Cms.Services. The booleans are
// booleans on the wire too — this API serializes enums as numbers, so the backend deliberately
// carries no enum here.

interface UnassignedHostBooking {
  bookingUid: string
  start: string
  end: string
  status: string
  siteName: string | null
  teamName: string
  teamSlug: string | null
  teamId: number
  eventTypeId: number
  eventTypeSlug: string
  hostUserId: number
  hostName: string | null
  hostEmail: string | null
  /** False when the host id on the booking matches no Cal account — not fixable by editing a roster. */
  hostKnownToCal: boolean
  /** On the team but never accepted. Cal ignores such a membership, so they are not really on it. */
  membershipPending: boolean
  /** Whether they are still in the event type's round robin. Context, not the reason the row exists. */
  hostsTheEventType: boolean
  hostCount: number
  attendeeName: string | null
}

interface UnassignedHostBookingReport {
  bookings: UnassignedHostBooking[]
  scannedBookingCount: number
  truncated: boolean
  windowStart: string
  windowEnd: string
}

function formatDateTime(value: string | null): string {
  if (!value) return "—"
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

/** What is wrong with this host, worst first. One row can trip more than one. */
function describeHost(row: UnassignedHostBooking): { label: string; variant: "destructive" | "secondary" | "outline" }[] {
  const badges: { label: string; variant: "destructive" | "secondary" | "outline" }[] = [
    row.membershipPending
      ? { label: "Membership not accepted", variant: "secondary" }
      : { label: "Not on the team", variant: "destructive" },
  ]

  if (!row.hostKnownToCal) {
    badges.push({ label: "Not set up for appointments", variant: "destructive" })
  }

  if (!row.hostsTheEventType) {
    badges.push({ label: "Not in the round robin", variant: "outline" })
  }

  return badges
}

// ── Component ────────────────────────────────────────────────────────

export default function UnassignedHostsPage() {
  const [report, setReport] = useState<UnassignedHostBookingReport | null>(null)
  const [loading, setLoading] = useState(true)

  // An inline error rather than the toast its sibling page uses. A toast that has already faded,
  // above a table reading "every appointment has a host", tells the reviewer the opposite of the
  // truth — and this page exists precisely to be believed when it says nothing is wrong.
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setReport(await api.get<UnassignedHostBookingReport>("/api/ams/bookings/unassigned-hosts"))
    } catch (err) {
      console.error("Failed to check upcoming appointments for unassigned hosts:", err)
      setError(err instanceof Error ? err.message : "Failed to check upcoming appointments.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const rows = report?.bookings ?? []

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconUserOff className="size-6" />
          <h1 className="text-2xl font-bold">Unassigned Hosts</h1>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? (
            <IconLoader2 className="size-4 animate-spin" />
          ) : (
            <IconRefresh className="size-4" />
          )}
          Refresh
        </Button>
      </div>
      <p className="text-muted-foreground max-w-3xl">
        Upcoming appointments whose host is not on the team that owns them. The appointment still
        stands and the household is still emailed about it; nobody on that site's roster is expected
        to turn up for it. Nothing here can be changed from this page — the fix is the team's membership, or
        moving the appointment.
      </p>

      {report?.truncated && (
        <div className="rounded-xl border bg-card p-4 text-sm">
          Only the first 250 upcoming appointments were checked, so this list may be short. Anything
          further out than {formatDateTime(report.windowEnd)} has not been looked at.
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border bg-card p-6">
          <div className="text-muted-foreground">
            Every upcoming appointment has a host who is on its site's team.
          </div>
          {report && (
            <div className="text-sm text-muted-foreground mt-1">
              Checked {report.scannedBookingCount}{" "}
              {report.scannedBookingCount === 1 ? "appointment" : "appointments"} between{" "}
              {formatDateTime(report.windowStart)} and {formatDateTime(report.windowEnd)}.
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Appointment</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>Why it is here</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                // A booking with two offending hosts is two rows, so the uid alone is not a key.
                <TableRow key={`${row.bookingUid}:${row.hostUserId}`}>
                  <TableCell>
                    <div className="font-medium">{formatDateTime(row.start)}</div>
                    <div className="text-sm text-muted-foreground">
                      {slotTypeLabelForSlug(row.eventTypeSlug)} ·{" "}
                      {row.attendeeName ?? "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>{row.siteName ?? row.teamName}</div>
                    {row.siteName === null && (
                      <div className="text-sm text-muted-foreground">{row.teamSlug ?? "—"}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {row.hostName ?? row.hostEmail ?? `Unknown host #${row.hostUserId}`}
                    </div>
                    <div className="text-sm text-muted-foreground">{row.hostEmail ?? "—"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {describeHost(row).map((badge) => (
                        <Badge key={badge.label} variant={badge.variant}>
                          {badge.label}
                        </Badge>
                      ))}
                    </div>
                    {row.hostCount > 1 && (
                      <div className="text-sm text-muted-foreground mt-1">
                        {row.hostCount - 1} other {row.hostCount === 2 ? "host" : "hosts"} on this
                        appointment
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
